<script>
  import { onMount } from "svelte";
  import { Modal, tooltip } from "svelte-utilities";
  import UndoIcon from "@local/assets/icons/undo.svg";
  import BlankIcon from "@local/assets/icons/blank.svg";
  import TextareaInput from "#lib/sidebar/TextareaInput.svelte";
  import SelectInput from "#src/lib/sidebar/SelectInput.svelte";
  import BooleanInput from "#src/lib/sidebar/BooleanInput.svelte";
  import eb from "#src/extensions/event-bus.js";
  import { profile } from "#src/stores/auth.js";
  import { alerts } from "#src/stores/alerts.js";

  import {
    singleOpeningLlmModel,
    singleOpeningReasoningLevel,
    pageAnalysisLlmModel,
    pageAnalysisReasoningLevel,
    tableImportLlmModel,
    tableImportReasoningLevel,
  } from "#src/stores/ui.js";

  import { api } from "#src/api";

  let modal;

  let prompts = {
    single_opening: null,
    page_analysis: null,
    table_import: null,
  };
  let tab = "single_opening";
  const tabs = ["single_opening", "page_analysis", "table_import"];
  const modelOptions = [
    { value: "claude-sonnet", label: "Claude Sonnet 4.6" },
    { value: "claude-opus", label: "Claude Opus 4.6" },
    { value: "google-gemini-pro", label: "Google Gemini Pro (3.1)" },
    { value: "openai-gpt-4", label: "OpenAI GPT 4" },
    { value: "openai-gpt-5.2", label: "OpenAI GPT 5.2" },
    { value: "openai-gpt-5.4-mini", label: "OpenAI GPT 5.4 mini" },
    { value: "openai-gpt-5.4", label: "OpenAI GPT 5.4" },
  ];
  const reasoningLevelOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];

  function open() {
    modal.open();
    fetchPrompts();
  }

  function findPrompt(prompts, name, user_id) {
    const userPrompt = prompts.find((p) => p.name === name && p.user_id === user_id);
    if (userPrompt) return { prompt: userPrompt.prompt, user_id };
    const defaultPrompt = prompts.find((p) => p.name === name && p.user_id == null);
    if (defaultPrompt) return { prompt: defaultPrompt.prompt, user_id: null };
    return null;
  }

  async function fetchPrompts() {
    const { data, error } = await api
      .from("llm_prompts")
      .select("*")
      .or(`user_id.is.null,user_id.eq.${$profile.id}`);
    if (error) {
      console.log("Error fetching prompts", error);
    } else {
      prompts.single_opening = findPrompt(data, "single_opening", $profile.id);
      prompts.page_analysis = findPrompt(data, "page_analysis", $profile.id);
      prompts.table_import = findPrompt(data, "table_import", $profile.id);
    }
  }

  async function updateUserPrompt(name, value, userDefined) {
    let newPrompt;
    if (userDefined) {
      newPrompt = await api
        .from("llm_prompts")
        .update({ prompt: value })
        .eq("name", name)
        .eq("user_id", $profile.id)
        .select("*")
        .single();
    } else {
      newPrompt = await api
        .from("llm_prompts")
        .insert({ name, prompt: value, user_id: $profile.id })
        .select("*")
        .single();
    }

    if (newPrompt.error) {
      alerts.add({ type: "error", message: "Error updating prompt" });
      console.log(newPrompt.error);
    } else {
      prompts[name] = { prompt: newPrompt.data.prompt, user_id: $profile.id };
    }
  }

  async function resetPrompt(name) {
    const del = await api.from("llm_prompts").delete().eq("name", name).eq("user_id", $profile.id);
    if (del.error) {
      alerts.add({ type: "error", message: "Error resetting prompt" });
      console.log("Error resetting prompt:", del.error);
    } else {
      fetchPrompts();
    }
  }

  onMount(() => {
    eb.on("open-ai-settings", open);

    return () => {
      eb.unsubscribe("open-ai-settings", open);
    };
  });
</script>

<Modal bind:this={modal} closeable width="32rem">
  <div slot="title">AI Settings</div>
  <div slot="content" class="text-xs">
    <div class="mb-4 flex justify-between border-b">
      <div class="flex items-center gap-4 setting-tabs">
        <button class:selected={tab === "single_opening"} on:click={() => (tab = "single_opening")}>
          Opening Import
        </button>
        <button class:selected={tab === "page_analysis"} on:click={() => (tab = "page_analysis")}>
          Page Analysis
        </button>
        <button class:selected={tab === "table_import"} on:click={() => (tab = "table_import")}>
          Table Extraction
        </button>
      </div>
    </div>
    <div class="space-y-4">
      <div>
        <div class="flex justify-between items-center px-2 pb-1">
          <div>Prompt</div>
          {#if prompts[tab]?.user_id}
            <button
              use:tooltip={{ text: "Reset to default prompt" }}
              on:click={() => resetPrompt(tab)}
              class="hover:bg-gray-200 rounded p-1">
              <UndoIcon />
            </button>
          {:else}
            <div class="p-1">
              <BlankIcon />
            </div>
          {/if}
        </div>
        <TextareaInput
          border
          rows={10}
          value={prompts[tab]?.prompt}
          fullwidth
          on:input={(e) => updateUserPrompt(tab, e.detail.value, !!prompts[tab]?.user_id)} />
      </div>
      {#if tab === "single_opening"}
        <SelectInput
          border
          labelWidth="7rem"
          options={modelOptions}
          bind:value={$singleOpeningLlmModel}
          label="AI Model" />
        <SelectInput
          border
          labelWidth="7rem"
          options={reasoningLevelOptions}
          bind:value={$singleOpeningReasoningLevel}
          label="Reasoning Effort" />
      {:else if tab === "page_analysis"}
        <SelectInput
          border
          labelWidth="7rem"
          options={modelOptions}
          bind:value={$pageAnalysisLlmModel}
          label="AI Model" />
        <SelectInput
          border
          labelWidth="7rem"
          options={reasoningLevelOptions}
          bind:value={$pageAnalysisReasoningLevel}
          label="Reasoning Effort" />
      {:else if tab === "table_import"}
        <SelectInput
          border
          labelWidth="7rem"
          options={modelOptions}
          bind:value={$tableImportLlmModel}
          label="AI Model" />
        <SelectInput
          border
          labelWidth="7rem"
          options={reasoningLevelOptions}
          bind:value={$tableImportReasoningLevel}
          label="Reasoning Effort" />
      {/if}
    </div>
  </div>
</Modal>

<style lang="scss">
  .setting-tabs {
    button {
      @apply text-gray-400 border-b border-transparent py-1;

      &.active,
      &:hover {
        @apply text-black;
      }

      &.selected {
        @apply border-black text-black;
      }
    }
  }
</style>
